import React from "react";

export function PageHeader({ label, title, subtitle, right }) {
  return (
    <div className="border-b border-border bg-white sticky top-14 md:top-0 z-30">
      <div className="px-4 md:px-8 py-4 md:py-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-6">
        <div>
          <div className="label-caps">{label}</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl leading-none mt-1 tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-2 font-mono">{subtitle}</p>
          )}
        </div>
        {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
      </div>
    </div>
  );
}

export function Card({ title, right, children, className = "" }) {
  return (
    <div className={`bg-white border border-border ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="label-caps">{title}</div>
          {right}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Stat({ label, value, unit, hint, accent }) {
  return (
    <div className="bg-white border border-border p-4 hover:-translate-y-[2px] hover:border-foreground transition-transform">
      <div className="label-caps">{label}</div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <div className={`font-display font-black text-3xl leading-none tracking-tight ${accent || ""}`}>
          {value}
        </div>
        {unit && <div className="label-caps">{unit}</div>}
      </div>
      {hint && <div className="mt-2 text-xs text-muted-foreground font-mono">{hint}</div>}
    </div>
  );
}

export function Btn({ children, variant = "primary", className = "", ...rest }) {
  const styles = {
    primary: "bg-foreground text-background hover:-translate-y-[1px]",
    ghost: "bg-transparent border border-border hover:bg-secondary",
    danger: "bg-destructive text-destructive-foreground hover:-translate-y-[1px]",
    outline: "bg-white border border-foreground hover:-translate-y-[1px]",
  };
  return (
    <button
      {...rest}
      className={`h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold transition-transform ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1.5">{label}</div>
      {children}
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full h-9 px-3 bg-white border border-border focus:border-foreground focus:outline-none font-mono text-sm ${props.className || ""}`}
    />
  );
}

export function Select({ children, ...rest }) {
  return (
    <select
      {...rest}
      className={`w-full h-9 px-2 bg-white border border-border focus:border-foreground focus:outline-none font-mono text-sm ${rest.className || ""}`}
    >
      {children}
    </select>
  );
}

/**
 * Combobox — type-to-filter searchable dropdown replacing native <select> for long lists.
 * Props:
 *   value        : selected id (string)
 *   onChange(id) : called with the selected id (or "" if cleared)
 *   options      : [{ value, label, hint? }]
 *   placeholder  : input placeholder when empty
 *   required     : native form-validation (hidden input mirror)
 *   disabled     : disable input
 *   testid       : data-testid on the trigger input
 *   emptyText    : shown when no option matches the query
 */
export function Combobox({ value, onChange, options = [], placeholder = "Search…", required = false, disabled = false, testid, emptyText = "No matches" }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const wrapRef = React.useRef(null);
  const listRef = React.useRef(null);
  const [activeIdx, setActiveIdx] = React.useState(0);

  const selected = React.useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 200);
    return options.filter((o) => (o.label || "").toLowerCase().includes(q) || (o.hint || "").toLowerCase().includes(q)).slice(0, 200);
  }, [options, query]);

  React.useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  React.useEffect(() => { setActiveIdx(0); }, [query, open]);

  const displayValue = open ? query : (selected?.label || "");

  const pick = (opt) => {
    onChange(opt.value);
    setQuery("");
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { if (filtered[activeIdx]) { e.preventDefault(); pick(filtered[activeIdx]); } }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        data-testid={testid}
        placeholder={placeholder}
        value={displayValue}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={onKey}
        className="w-full h-9 px-3 bg-white border border-border focus:border-foreground focus:outline-none font-mono text-sm"
      />
      {selected && !open && (
        <button
          type="button"
          onClick={() => { onChange(""); setQuery(""); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive text-xs font-mono"
          tabIndex={-1}
          aria-label="Clear"
        >
          ×
        </button>
      )}
      {open && (
        <div ref={listRef} className="absolute z-30 left-0 right-0 mt-1 bg-white border border-border shadow-lg max-h-72 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground font-mono">{emptyText}</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`px-3 py-1.5 text-sm font-mono cursor-pointer ${i === activeIdx ? "bg-foreground text-background" : "hover:bg-secondary"}`}
              >
                <div>{opt.label}</div>
                {opt.hint && <div className={`text-[10px] ${i === activeIdx ? "text-background/70" : "text-muted-foreground"}`}>{opt.hint}</div>}
              </div>
            ))
          )}
        </div>
      )}
      {/* Native mirror for form validation only. */}
      {required && (
        <input tabIndex={-1} aria-hidden className="sr-only" required value={value || ""} onChange={() => {}} />
      )}
    </div>
  );
}

export function Empty({ text }) {
  return (
    <div className="py-16 text-center text-muted-foreground text-sm font-mono">
      {text}
    </div>
  );
}
